import { call, put, SagaReturnType, select } from 'redux-saga/effects';
import { getGrowthBook } from '../../../services/growthbook';
import { themeSettingsSelector } from '../../reducers/app/configuration';
import FEATURES from '../../../constants/featureToggling';
import { containerSelector } from '../../reducers/app/params';
import { setIsThemeApplied } from '../../actions/app/initialization';
import { logger } from '../experiment';

let themeModule = null;

export function* getThemeSettings() {
  const growthbook = getGrowthBook();
  const {
    additionalServiceClassName: defaultAdditionalServiceClassName = '',
    mode: defaultMode,
    theme: defaultTheme,
    unmountMode: defaultUnmountMode,
  }: ReturnType<typeof themeSettingsSelector> = yield select(
    themeSettingsSelector
  );

  try {
    const themeSettingsFeatureValue = growthbook?.getFeatureValue?.<
      SagaReturnType<typeof themeSettingsSelector>
    >(FEATURES.THEME_SETTINGS, {} as any);
    const additionalServiceClassName =
      themeSettingsFeatureValue.additionalServiceClassName;
    const mode = themeSettingsFeatureValue.mode;
    const theme = themeSettingsFeatureValue.theme;
    const unmountMode = themeSettingsFeatureValue.unmountMode;

    return {
      additionalServiceClassName:
        additionalServiceClassName ?? defaultAdditionalServiceClassName,
      mode: mode ?? defaultMode,
      theme: theme ?? defaultTheme,
      unmountMode: unmountMode ?? defaultUnmountMode,
    };
  } catch (e) {
    return {
      additionalServiceClassName: defaultAdditionalServiceClassName,
      mode: defaultMode,
      theme: defaultTheme,
      unmountMode: defaultUnmountMode,
    };
  }
}

const isDisabledUnmountMode = () => {
  const growthbook = getGrowthBook();
  return growthbook?.isOn(FEATURES.DISABLE_UNMOUNT_MODE);
};

export function* resetThemeMode() {
  const { theme, unmountMode } = yield call(getThemeSettings);

  if (isDisabledUnmountMode()) {
    // need to call toggle(false) to clear after service on destroy
    themeModule?.[theme]?.toggle?.(false);
  } else {
    if (unmountMode) {
      themeModule?.[theme]?.[unmountMode]?.();
    }
  }
}

export function* themeWatcher() {
  const container: ReturnType<typeof containerSelector> = yield select(
    containerSelector
  );
  const DOM_NODE = document.querySelector(container);
  const {
    additionalServiceClassName,
    mode,
    theme,
  }: SagaReturnType<typeof getThemeSettings> = yield call(getThemeSettings);

  if (DOM_NODE) {
    DOM_NODE.classList?.add?.(
      'live-casino-service',
      `live-casino-${theme || ''}`
    );

    if (additionalServiceClassName) {
      DOM_NODE.classList?.add?.(additionalServiceClassName);
    }
  } else {
    logger.warn(
      'Unable to find root DOM node for adding required CSS classes.'
    );
  }

  yield call(async function () {
    try {
      await initTheme(theme, mode);
    } catch (e) {
      logger.warn(`Unable to turn on theme "${theme}" with mode ${mode}`);
    }
  });

  yield put(setIsThemeApplied(true));
}

async function initTheme(themeName: string, mode: string): Promise<void> {
  themeModule = await import(`@betbook/modulor-brands/dist/${themeName}.js`);
  themeModule?.[themeName]?.[mode]?.();
  themeModule?.[themeName]?.toggle?.(true);
}
